import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { usePanels } from '../../shared/hooks/use-panels';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useBrandKit } from '../../brand-kit';
import { C, FONT_UI } from '../../constants/design-tokens';
import type { Fill } from '../../shared/utils/fill';
import type { SobreimpresoPreset } from '../../brand-kit/types';

const fillToCss = (fill: Fill): string => {
  switch (fill.kind) {
    case 'solid':
      return fill.color;
    case 'linear': {
      const cssAngle = (fill.angle + 90) % 360;
      const stops = fill.stops.map(s => `${s.color} ${Math.round(s.offset * 100)}%`).join(', ');
      return `linear-gradient(${cssAngle}deg, ${stops})`;
    }
    case 'radial': {
      const stops = fill.stops.map(s => `${s.color} ${Math.round(s.offset * 100)}%`).join(', ');
      return `radial-gradient(circle, ${stops})`;
    }
  }
};

const PREVIEW_FONT_SIZE = 14;

const PresetCard: React.FC<{ preset: SobreimpresoPreset; onSelect: () => void }> = ({ preset, onSelect }) => {
  const { config } = preset;
  const [hover, setHover] = React.useState(false);

  const ratio = PREVIEW_FONT_SIZE / config.fontSize;

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 8,
        padding: 10,
        background: hover ? C.accentSurface : C.surface,
        border: `1px solid ${hover ? C.accent : C.border}`,
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'background 0.15s, border-color 0.15s'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 52,
          padding: '4px 0',
          overflow: 'hidden'
        }}
      >
        <span
          style={{
            display: 'inline-block',
            background: fillToCss(config.fill),
            color: config.textFill,
            fontFamily: config.fontFamily,
            fontWeight: config.fontWeight ?? 700,
            fontStyle: config.fontStyle ?? 'normal',
            fontSize: PREVIEW_FONT_SIZE,
            lineHeight: 1.2,
            paddingLeft: (config.paddingX ?? 32) * ratio,
            paddingRight: (config.paddingX ?? 32) * ratio,
            paddingTop: (config.paddingY ?? 16) * ratio,
            paddingBottom: (config.paddingY ?? 16) * ratio,
            borderRadius: (config.borderRadius ?? 32) * ratio,
            border:
              config.strokeWidth && config.strokeWidth > 0
                ? `${Math.max(1, config.strokeWidth * ratio)}px solid ${config.stroke ?? '#000'}`
                : undefined,
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {config.text}
        </span>
      </div>
      <span
        style={{
          fontSize: 10,
          color: C.textSecondary,
          fontFamily: FONT_UI,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {preset.name}
      </span>
    </button>
  );
};

export const SobreimpresoPresetsPanel: React.FC = () => {
  const { isOpen, close } = usePanels();
  const { addSobreimpreso } = useEditorCanvas();
  const { sobreimpresoPresets } = useBrandKit();

  if (!isOpen('sobreimpresoPresets')) return null;
  if (!sobreimpresoPresets?.length) return null;

  const handleSelect = (preset: SobreimpresoPreset) => {
    addSobreimpreso(preset.config);
    close();
  };

  return createPortal(
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.35)',
          backdropFilter: 'blur(2px)',
          zIndex: 1000
        }}
      />
      <div
        data-panel-id='sobreimpresoPresets'
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(90vw, 350px)',
          maxHeight: '80vh',
          background: C.panelBg,
          border: `1px solid ${C.panelBorder}`,
          borderRadius: 14,
          padding: 20,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
          fontFamily: FONT_UI,
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18
          }}
        >
          <h3 style={{ color: C.textPrimary, fontSize: 15, fontWeight: 600, margin: 0 }}>Elegí un estilo</h3>
          <button
            onClick={close}
            style={{
              width: 28,
              height: 28,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 10,
            overflowY: 'auto'
          }}
        >
          {sobreimpresoPresets.map(preset => (
            <PresetCard key={preset.id} preset={preset} onSelect={() => handleSelect(preset)} />
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};
